import React, { FormEvent, useState } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import SummaryCard from '../common/SummaryCard';
import SourceCombobox from '../common/SourceCombobox';
import {
  preventInvalidNumberInput,
  preventInvalidNumberPaste,
} from '../../utils/validationMessages';

interface CreateIBFormProps {
  onSubmit?: (data: IBData) => void;
}

export interface IBData {
  name: string;
  email: string;
  phone: string;
  tier: 'Master IB' | 'Sub IB';
  parentIB?: string;
  commissionRate: number;
  status: 'active' | 'inactive' | 'pending';
}

const ibTierOptions = [
  { value: 'Master IB', label: 'Master IB' },
  { value: 'Sub IB', label: 'Sub IB' },
] as const;

const ibStatusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending' },
] as const;

const CreateIBForm = ({ onSubmit }: CreateIBFormProps) => {
  const [formData, setFormData] = useState<IBData>({
    name: '',
    email: '',
    phone: '',
    tier: 'Sub IB',
    parentIB: '',
    commissionRate: 0,
    status: 'pending',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof IBData, string>>>(
    {}
  );

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof IBData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (formData.commissionRate < 0 || formData.commissionRate > 100) {
      newErrors.commissionRate = 'Commission rate must be between 0 and 100';
    }

    if (formData.tier === 'Sub IB' && !formData.parentIB?.trim()) {
      newErrors.parentIB = 'Parent IB is required for Sub IB';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (validateForm() && onSubmit) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof IBData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-drawer-form="true">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Personal Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name*</Label>
            <Input
              id="name"
              placeholder="Enter full name"
              value={formData.name}
              onChange={e => handleInputChange('name', e.target.value)}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address*</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={e => handleInputChange('email', e.target.value)}
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number*</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="Enter phone number"
              value={formData.phone}
              onChange={e => handleInputChange('phone', e.target.value)}
              className={errors.phone ? 'border-red-500' : ''}
            />
            {errors.phone && (
              <p className="text-sm text-red-500">{errors.phone}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">IB Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tier">IB Tier*</Label>
              <SourceCombobox
                id="tier"
                items={ibTierOptions.map(option => option.label)}
                placeholder="Select IB tier"
                value={formData.tier}
                onChange={value => {
                  const option = ibTierOptions.find(
                    item => item.label === value
                  );
                  if (option) {
                    handleInputChange('tier', option.value);
                  }
                }}
                emptyText="No IB tiers found."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status*</Label>
              <SourceCombobox
                id="status"
                items={ibStatusOptions.map(option => option.label)}
                placeholder="Select status"
                value={
                  ibStatusOptions.find(
                    option => option.value === formData.status
                  )?.label || ''
                }
                onChange={value => {
                  const option = ibStatusOptions.find(
                    item => item.label === value
                  );
                  if (option) {
                    handleInputChange('status', option.value);
                  }
                }}
                emptyText="No statuses found."
              />
            </div>
          </div>

          {formData.tier === 'Sub IB' && (
            <div className="space-y-2">
              <Label htmlFor="parentIB">Parent IB*</Label>
              <Input
                id="parentIB"
                placeholder="Enter parent IB name or ID"
                value={formData.parentIB}
                onChange={e => handleInputChange('parentIB', e.target.value)}
                className={errors.parentIB ? 'border-red-500' : ''}
              />
              {errors.parentIB && (
                <p className="text-sm text-red-500">{errors.parentIB}</p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="commissionRate">Commission Rate (%)</Label>
            <Input
              id="commissionRate"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={formData.commissionRate}
              onKeyDown={preventInvalidNumberInput}
              onPaste={preventInvalidNumberPaste}
              onChange={e =>
                handleInputChange(
                  'commissionRate',
                  parseFloat(e.target.value) || 0
                )
              }
              className={errors.commissionRate ? 'border-red-500' : ''}
            />
            {errors.commissionRate && (
              <p className="text-sm text-red-500">{errors.commissionRate}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <SummaryCard
        title="IB Summary"
        items={[
          { label: 'Name', value: formData.name || '-' },
          { label: 'Email', value: formData.email || '-' },
          { label: 'Tier', value: formData.tier },
          { label: 'Status', value: formData.status },
          { label: 'Commission Rate', value: `${formData.commissionRate}%` },
          ...(formData.tier === 'Sub IB'
            ? [{ label: 'Parent IB', value: formData.parentIB || '-' }]
            : []),
        ]}
      />
    </form>
  );
};

export default CreateIBForm;
